import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { TextField, TextFieldProps } from "../../TextField";
import { TimeFormat } from "./types";
import timePlaceholderMask from "./maskito/timePlaceholderMask";
import { useMaskito } from "@maskito/react";
import { useMergeRefs } from "../../../hooks";
import {
  CLOCK_ICON,
  TIME_CONSTANTS,
  TIME_FORMAT_PLACEHOLDERS,
} from "./constants";
import { Icon } from "../../Icon";
import { convertTimeFormat, parseInputValue } from "./utils/timeParsing";
import { isPartialInputPotentiallyValid } from "./utils/timeValidation";

import cx from "classnames";
import styles from "../TimeField.module.scss";

/**
 * Change event data for MaskedTimeInput onInputChange callback.
 */
export type MaskedTimeInputChange = {
  /**
   * The original input change event.
   */
  event: React.ChangeEvent<HTMLInputElement>;
  /**
   * The parsed time as a string if valid, otherwise null.
   */
  time: string | null;
  /**
   * True if the input matches the mask and is not empty.
   */
  isInputValid: boolean;
  /**
   * True if the input is empty.
   */
  isInputEmpty: boolean;
};

/**
 * Props for the MaskedTimeInput component.
 * @extends Omit<TextFieldProps, "onChange" | "value" | "ref" | "showCounter" | "prefix" | "type" | "maxLength" | "hint">
 */
export interface MaskedTimeInputProps
  extends Omit<
    TextFieldProps,
    | "onChange"
    | "value"
    | "ref"
    | "showCounter"
    | "prefix"
    | "type"
    | "maxLength"
    | "hint"
  > {
  /**
   * The last valid time as a string.
   */
  lastValidTime: string | null;
  /**
   * The time format (12 or 24 hour).
   */
  format: TimeFormat;
  /**
   * The placeholder string for the input.
   */
  placeholder: string;
  /**
   * Minimum allowed time constraint.
   */
  min?: string;
  /**
   * Maximum allowed time constraint.
   */
  max?: string;
  /**
   * Callback fired when the input value changes.
   */
  onInputChange?: (change: MaskedTimeInputChange) => void;
  /**
   * Callback fired when a key is pressed.
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Callback fired when the input receives focus.
   */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Data attribute indicating if the current input is valid.
   */
  "data-input-valid"?: boolean;
}

/**
 * A masked time input component for entering time in 12/24 hour format.
 *
 * Features:
 * - Input masking and placeholder using Maskito
 * - Smart validation and parsing
 * - Hard-coded clock icon prefix
 * - Controlled or uncontrolled usage
 * - Automatic time format conversion
 * - Placeholder handling and cleanup
 *
 * @example
 * <MaskedTimeInput
 *   lastValidTime="14:30"
 *   format={24}
 *   placeholder="––:––"
 *   onInputChange={(change) => {
 *     console.log('Input changed:', change.time);
 *   }}
 * />
 */
export const MaskedTimeInput = forwardRef<
  HTMLInputElement,
  MaskedTimeInputProps
>(
  (
    {
      lastValidTime,
      format,
      placeholder,
      min,
      max,
      onInputChange,
      onKeyDown,
      onFocus,
      className,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(placeholder);

    const { options, removePlaceholder } = timePlaceholderMask({
      format,
      placeholder,
    });
    const maskedInputRef = useMaskito({ options });
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useMergeRefs([maskedInputRef, inputRef, ref]);
    const previousTimeRef = useRef<string | null>(null);
    const isUserTypingRef = useRef(false);
    const inputValueRef = useRef<string>(placeholder);
    const previousPlaceholderRef = useRef<string>(placeholder);

    // Parse current input value - following DateFields pattern
    const currentParsedData = useMemo(() => {
      return parseInputValue(inputValue, format, removePlaceholder, min, max);
    }, [inputValue, format, removePlaceholder, min, max]);

    // Update refs when values change
    useEffect(() => {
      inputValueRef.current = inputValue;
    }, [inputValue]);

    useEffect(() => {
      previousPlaceholderRef.current = placeholder;
    }, [placeholder]);

    // Update input value when lastValidTime changes (for controlled components)
    // Only update if user is not currently typing
    useEffect(() => {
      if (lastValidTime === undefined) return;
      if (lastValidTime === previousTimeRef.current) return;
      if (isUserTypingRef.current) return;

      const newInputValue = lastValidTime
        ? convertTimeFormat(lastValidTime, format)
        : placeholder;

      setInputValue(newInputValue);
      inputValueRef.current = newInputValue;
      previousTimeRef.current = lastValidTime;
    }, [lastValidTime, format, placeholder]);

    // Handle placeholder changes
    useEffect(() => {
      // Only run when placeholder actually changes
      if (placeholder === previousPlaceholderRef.current) return;

      // If we have a valid time, don't override it with placeholder
      if (lastValidTime && inputValue === lastValidTime) return;

      // Only update if input is actually a placeholder
      const isOldPlaceholder =
        inputValue === TIME_FORMAT_PLACEHOLDERS[12] ||
        inputValue === TIME_FORMAT_PLACEHOLDERS[24];

      if (isOldPlaceholder) {
        setInputValue(placeholder);
      }

      previousPlaceholderRef.current = placeholder;
    }, [placeholder, lastValidTime, inputValue]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        isUserTypingRef.current = true;

        const { time, isInputValid, isInputEmpty } = parseInputValue(
          event.target.value,
          format,
          removePlaceholder,
          min,
          max,
        );

        // Check if this might be a potentially valid partial input
        const isPotentiallyValidPartial =
          !isInputValid &&
          !isInputEmpty &&
          event.target.value.length >= placeholder.length && // User is typing, not deleting
          isPartialInputPotentiallyValid(event.target.value, format, min, max);

        // Only update the input value if it's valid, empty, or potentially valid partial
        if (isInputValid || isInputEmpty || isPotentiallyValidPartial) {
          setInputValue(event.target.value);
        } else {
          // Reset to placeholder when input is invalid to clear the mask's internal state
          setInputValue(placeholder);
        }

        onInputChange?.({
          event,
          time,
          isInputValid,
          isInputEmpty,
        });

        // Reset typing flag after a delay
        setTimeout(() => {
          isUserTypingRef.current = false;
        }, TIME_CONSTANTS.INPUT_DEBOUNCE_TIMEOUT);
      },
      [format, removePlaceholder, onInputChange, min, max, placeholder],
    );

    const resetTypingFlag = useCallback(() => {
      isUserTypingRef.current = false;
    }, []);

    useImperativeHandle(
      ref,
      () =>
        ({
          ...inputRef.current,
          focus: () => inputRef.current?.focus(),
          resetTypingFlag,
        }) as HTMLInputElement & { resetTypingFlag: () => void },
    );

    const classNames = useMemo(() => {
      return cx(styles["time-field"], className);
    }, [className]);

    return (
      <TextField
        ref={combinedRef}
        data-input-valid={currentParsedData.isInputValid}
        data-input-empty={currentParsedData.isInputEmpty}
        data-time={currentParsedData.time ?? ""}
        value={inputValue}
        onChange={handleChange}
        prefix={<Icon svg={CLOCK_ICON} />}
        autoComplete="off"
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        className={classNames}
        {...props}
      />
    );
  },
);

MaskedTimeInput.displayName = "MaskedTimeInput";
